import {
  AREA_UTIL,
  LAYOUTS,
  type EstadoDoTrabalhoDeImpressao,
  type ImprimirFolha,
  type ImprimirMarcadores,
} from "@foto/shared";
import { FORMATO_DOS_MARCADORES, descreverFormato, type TrabalhoDaFila } from "./fila.js";
import {
  pedidoDaFolha,
  quantasFotos,
  quantoTemNaFolha,
  quantosMarcadores,
  type Folha,
} from "./folhas.js";
import {
  folhasPorExtenso,
  limitarCopias,
  numeroEmPortugues,
  resumoDaConfirmacao,
} from "./impressora.js";

/**
 * O que o painel de imprimir imprime, sem React.
 *
 * O painel serve a duas telas: a **folha montada** na tela de Impressão e a **folha de marcadores** de um
 * conjunto já feito. Tudo o que muda de uma para a outra mora aqui — o subtítulo, a frase de confirmação,
 * o aviso, o que dizer quando não há nada a imprimir e a chamada ao servidor —, e o painel só pergunta. Assim
 * a lógica de verificar a impressora, escolher as cópias e acompanhar a fila fica uma só, e esta parte, que
 * é regra de texto e de contrato, se testa sem navegador.
 */

export type AlvoDeImpressao =
  | {
      tipo: "folha";
      folha: Folha;
      /** Qual das folhas montadas é esta (a partir de 1). */
      numeroDaFolha: number;
      totalDeFolhas: number;
    }
  | {
      tipo: "marcadores";
      conjuntoId: number;
      /** Quantas faixas a folha do conjunto tem (três). */
      faixas: number;
      /** As que a IA não entregou: ficam em branco no papel. */
      faixasSemArte: number;
    };

/** Quantas faixas do conjunto têm arte, sem sair do intervalo possível. */
const faixasComArte = (alvo: Extract<AlvoDeImpressao, { tipo: "marcadores" }>): number =>
  Math.min(alvo.faixas, Math.max(0, alvo.faixas - alvo.faixasSemArte));

/** Quantas fotos (ou faixas com arte) a folha leva: com zero, não há o que imprimir. */
export function fotosDoAlvo(alvo: AlvoDeImpressao): number {
  return alvo.tipo === "folha" ? quantoTemNaFolha(alvo.folha) : faixasComArte(alvo);
}

/** Como o servidor rotula o trabalho: o formato da folha montada, ou "marcadores". */
export function formatoDoAlvo(alvo: AlvoDeImpressao): string {
  return alvo.tipo === "folha" ? alvo.folha.layout : FORMATO_DOS_MARCADORES;
}

// ─── O que o painel diz ───────────────────────────────────────────────────────

/** A linha sob o título do painel: qual folha é, e o que ela leva. */
export function subtituloDoPainel(alvo: AlvoDeImpressao): string {
  if (alvo.tipo === "marcadores") {
    return [
      "A folha do conjunto",
      descreverFormato(FORMATO_DOS_MARCADORES, faixasComArte(alvo)),
    ].join(" · ");
  }
  const fotos = quantasFotos(alvo.folha);
  const marcadores = quantosMarcadores(alvo.folha);
  const conteudo = [
    `${fotos} ${fotos === 1 ? "foto" : "fotos"}`,
    ...(marcadores > 0 ? [`${marcadores} ${marcadores === 1 ? "marcador" : "marcadores"}`] : []),
  ].join(" e ");
  return [
    alvo.totalDeFolhas > 1
      ? `Folha ${alvo.numeroDaFolha} de ${alvo.totalDeFolhas}`
      : "A folha montada",
    `${LAYOUTS[alvo.folha.layout].nome}, ${conteudo}`,
  ].join(" · ");
}

/** O que dizer quando não há nada a imprimir — e o remédio, que muda: foto na folha, ou arte no conjunto. */
export function motivoDeFolhaVazia(alvo: AlvoDeImpressao): string {
  return alvo.tipo === "marcadores"
    ? "Nenhuma faixa deste conjunto tem arte: use “Refazer este” antes de imprimir."
    : "A folha está vazia: coloque ao menos uma foto antes de imprimir.";
}

/** "Este conjunto tem 1 faixa sem arte: ela sai em branco na folha." */
export function avisoDeFaixasSemArte(semArte: number): string | null {
  if (semArte <= 0) return null;
  return semArte === 1
    ? "Este conjunto tem 1 faixa sem arte: ela sai em branco na folha."
    : `Este conjunto tem ${semArte} faixas sem arte: elas saem em branco na folha.`;
}

/** O que o papel imprime e mantém: a folha dos marcadores foi montada dentro dele. */
export const NOTA_DA_AREA_UTIL = `A folha usa a área útil do papel (${numeroEmPortugues(String(AREA_UTIL.larguraMm))} × ${numeroEmPortugues(String(AREA_UTIL.alturaMm))} mm).`;

export interface ConfirmacaoDoAlvo {
  /** A frase que fecha o painel antes de gastar papel e fita. */
  frase: string;
  /** O que convém saber junto, em linhas curtas. */
  notas: string[];
  /** Algo que vai sair diferente do esperado e merece destaque próprio; `null` quando não há. */
  aviso: string | null;
}

/**
 * A confirmação antes de imprimir.
 *
 * A folha montada segue `resumoDaConfirmacao` como sempre (os espaços sem foto são uma nota). A de
 * marcadores diz "folhas de marcadores", lembra a área útil do papel — é o que o operador confere no papel
 * — e põe a faixa sem arte num **aviso**, não numa nota: é um defeito do conjunto, e ninguém deve descobri-lo
 * só ao pegar a folha.
 */
export function confirmacaoDoAlvo(
  alvo: AlvoDeImpressao,
  copias: number,
  impressora: string,
): ConfirmacaoDoAlvo {
  if (alvo.tipo === "folha") {
    const r = resumoDaConfirmacao({
      copias,
      layout: alvo.folha.layout,
      // Fotos e marcadores: o espaço de marcador cheio não é "espaço sem foto".
      fotosNaFolha: quantoTemNaFolha(alvo.folha),
      impressora,
      folha: { numero: alvo.numeroDaFolha, total: alvo.totalDeFolhas },
    });
    return { ...r, aviso: null };
  }

  return {
    frase: `Vai imprimir ${folhasPorExtenso(limitarCopias(copias))} de marcadores, ${alvo.faixas} por folha, em ${impressora}.`,
    notas: ["Cada folha gasta papel e fita da impressora.", NOTA_DA_AREA_UTIL],
    // Sem arte em faixa nenhuma o painel já barra o envio, com o remédio: repetir o aviso só faria barulho.
    aviso: faixasComArte(alvo) === 0 ? null : avisoDeFaixasSemArte(alvo.faixasSemArte),
  };
}

// ─── Enviar ───────────────────────────────────────────────────────────────────

/** O que o servidor devolve ao aceitar um trabalho: o número dele e que está esperando a vez. */
export interface RespostaDoEnvio {
  id: number;
  estado: EstadoDoTrabalhoDeImpressao;
  copias: number;
}

type Envio<Entrada> = { mutate: (entrada: Entrada) => Promise<RespostaDoEnvio> };

/** O pedaço do cliente tRPC que o envio usa: cada tela fala com um procedimento, e o resto não importa. */
export interface ClienteDeEnvio {
  impressao: { imprimir: Envio<ImprimirFolha> };
  marcadores: { imprimir: Envio<ImprimirMarcadores> };
}

/**
 * Manda o alvo à impressora. A folha montada vai por `impressao.imprimir`, com o pedido que o servidor monta
 * de novo; a de marcadores, por `marcadores.imprimir`, só com o conjunto — o arquivo já existe no servidor, e é
 * esse mesmo que "Baixar JPEG" entrega.
 */
export function enviarAlvo(
  cliente: ClienteDeEnvio,
  alvo: AlvoDeImpressao,
  destino: { impressoraId: number; copias: number },
): Promise<RespostaDoEnvio> {
  const copias = limitarCopias(destino.copias);
  if (alvo.tipo === "marcadores") {
    return cliente.marcadores.imprimir.mutate({
      id: alvo.conjuntoId,
      impressoraId: destino.impressoraId,
      copias,
    });
  }
  return cliente.impressao.imprimir.mutate({
    ...pedidoDaFolha(alvo.folha),
    impressoraId: destino.impressoraId,
    copias,
  });
}

/**
 * O trabalho como a tela o conhece no instante em que o servidor o aceita, antes de a fila de verdade
 * voltar: foi aceito, aguarda. Só `id`, `estado` e `copias` vêm do servidor; o resto é o que se sabe do pedido.
 */
export function trabalhoRecemEnviado(
  alvo: AlvoDeImpressao,
  resposta: RespostaDoEnvio,
  impressoraNome: string,
  agora: Date,
): TrabalhoDaFila {
  return {
    id: resposta.id,
    impressoraNome,
    layout: formatoDoAlvo(alvo),
    fotos: fotosDoAlvo(alvo),
    copias: resposta.copias,
    copiaAtual: 0,
    estado: resposta.estado,
    detalhe: null,
    createdAt: agora.toISOString(),
    iniciadoEm: null,
    concluidoEm: null,
  };
}

// ─── Para onde vai quem precisa cadastrar uma impressora ──────────────────────

/**
 * O cadastro de impressoras mora na tela de Impressão (só o administrador o vê). Quem está em Marcadores e
 * não tem impressora é levado até lá, com o painel de impressoras já aberto.
 */
export const ROTA_DAS_IMPRESSORAS = "/impressao?painel=impressoras";

/** A tela de Impressão foi chamada para abrir o painel de impressoras? */
export function pedeOPainelDeImpressoras(parametros: URLSearchParams): boolean {
  return parametros.get("painel") === "impressoras";
}
